import { Component, Input, forwardRef, OnDestroy, AfterViewInit, ElementRef } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { DIRECTION, Direction } from "../types/quantity-selector";

@Component({
  selector: "ds-quantity-selector",
  templateUrl: "./ds-quantity-selector.component.html",
  styleUrls: ["./ds-quantity-selector.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DsQuantitySelectorComponent),
      multi: true,
    },
  ],
})
export class DsQuantitySelectorComponent implements ControlValueAccessor, OnDestroy, AfterViewInit {
  @Input() step: number = 1;
  @Input() disabled: boolean = false;
  @Input() label: string = "";
  @Input() hintText: string = "";
  @Input() required: boolean = false;
  @Input() error: boolean = false;
  @Input() placeholder: string = "";
  @Input() min: number = 1;
  @Input() max: number = 100;
  is_input_error = false;
  error_input_message = "";
  public DIRECTION = DIRECTION;

  private _value: number | undefined;
  private autoIncrementInterval: any = null;
  private autoIncrementTimeout: any = null;
  private isAutoIncrementing = false;
  private lastDirection: Direction | null = null;
  private contextMenuListeners: Array<{ element: Element; handler: (e: Event) => void }> = [];

  get value(): number | undefined {
    if (this._value === undefined || isNaN(this._value)) {
      return undefined;
    }
    return this._value;
  }

  set value(val: number | undefined) {
    if (val === undefined) {
      this._value = undefined;
      this.onChange(undefined as any);
    } else if (isNaN(val)) {
      this._value = undefined;
      this.onChange(undefined as any);
    } else {
      this._value = val;
      this.onChange(val);
    }
  }

  private onChange = (value: number | undefined) => {};
  private onTouched = () => {};

  constructor(private elementRef: ElementRef) {
    this._value = undefined;
  }

  increment(): void {
    if (!this.disabled && !this.isMaxValue()) {
      if (this.value === undefined || isNaN(this.value as any)) {
        this.value = this.min;
      } else {
        const newValue = parseFloat(this.value.toString()) + parseFloat(this.step.toString());
        this.value = Math.min(Math.max(newValue, this.min), this.max);
      }
      this.is_input_error = false;
    }
  }

  decrement(): void {
    if (!this.disabled && !this.isMinValue()) {
      if (this.value === undefined || isNaN(this.value as any)) {
        this.value = this.min;
      } else {
        const newValue = parseFloat(this.value.toString()) - parseFloat(this.step.toString());
        this.value = Math.max(Math.min(newValue, this.max), this.min);
      }
      this.is_input_error = false;
    }
  }

  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const inputValue = target.value.trim();

    if (inputValue === "" || inputValue === "-") {
      this.value = undefined;
      this.is_input_error = false;
      return;
    }

    const newValue = parseFloat(inputValue);

    if (isNaN(newValue)) {
      this.value = undefined;
      this.is_input_error = false;
      return;
    }
    this.value = newValue;
    if (newValue < this.min || newValue > this.max) {
      this.is_input_error = true;
      this.error_input_message =
        newValue < this.min
          ? "Ingresa un valor mayor o igual a " + this.min
          : "Ingresa un valor menor o igual a " + this.max;
      return;
    }
    this.is_input_error = false;
  }

  onBlur(): void {
    this.onTouched();
  }

  isMaxValue(): boolean {
    if (this.value === undefined || isNaN(this.value as any)) {
      return false;
    }
    return this.value >= this.max || this.disabled;
  }

  isMinValue(): boolean {
    if (this.value === undefined || isNaN(this.value as any)) {
      return false;
    }
    return this.value <= this.min || this.disabled;
  }

  writeValue(value: number | null | undefined): void {
    if (value == null || isNaN(value)) {
      this._value = undefined;
    } else {
      this._value = value;
    }
  }

  registerOnChange(fn: (value: number | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  startAutoIncrement(direction: Direction, event?: Event): void {
    event?.preventDefault();
    if (this.disabled || this.isAutoIncrementing) return;

    this.isAutoIncrementing = true;
    this.lastDirection = direction;

    this.autoIncrementTimeout = setTimeout(() => {
      if (this.isAutoIncrementing) {
        this.autoIncrementInterval = setInterval(() => {
          if (direction === DIRECTION.Up) {
            this.increment();
          } else {
            this.decrement();
          }
        }, 100);
      }
    }, 500);
  }

  stopAutoIncrement(event?: Event): void {
    event?.preventDefault();
    if (this.isAutoIncrementing && this.autoIncrementTimeout && !this.autoIncrementInterval) {
      if (this.lastDirection === DIRECTION.Up) {
        this.increment();
      } else if (this.lastDirection === DIRECTION.Down) {
        this.decrement();
      }
    }
    this.isAutoIncrementing = false;
    this.lastDirection = null;
    if (this.autoIncrementTimeout) {
      clearTimeout(this.autoIncrementTimeout);
      this.autoIncrementTimeout = null;
    }
    if (this.autoIncrementInterval) {
      clearInterval(this.autoIncrementInterval);
      this.autoIncrementInterval = null;
    }
  }

  ngAfterViewInit(): void {
    const componentElement = this.elementRef.nativeElement;
    const buttons = componentElement.querySelectorAll(".quantity-buttons");
    buttons.forEach((btn: Element) => {
      const handler = (e: Event) => e.preventDefault();
      btn.addEventListener("contextmenu", handler);
      this.contextMenuListeners.push({ element: btn, handler });
    });
  }

  ngOnDestroy(): void {
    this.stopAutoIncrement();
    this.contextMenuListeners.forEach(({ element, handler }) => {
      element.removeEventListener("contextmenu", handler);
    });
    this.contextMenuListeners = [];
  }
}
